import React, { useCallback } from 'react';
import { Analytics, SourceType } from '@trello/atlassian-analytics';
import { forNamespace } from '@trello/i18n';
import {
  Popover,
  PopoverProps,
  UsePopoverResult,
} from '@trello/nachos/popover';
import { TestId } from '@trello/test-ids';
import {
  CreateBoardPopoverScreenId,
  CreateBoardPopoverScreens,
} from './CreateBoardPopoverScreens';

const viewTitle = forNamespace('view title');

export const screenAnalyticsNames: {
  [key: number]:
    | 'createFromTemplateInlineDialog'
    | 'useTemplateInlineDialog'
    | 'createBoardInlineDialog';
} = {
  [CreateBoardPopoverScreenId.CreateBoard]: 'createBoardInlineDialog',
  [CreateBoardPopoverScreenId.CreateBoardFromTemplate]:
    'useTemplateInlineDialog',
  [CreateBoardPopoverScreenId.SelectTemplate]: 'createFromTemplateInlineDialog',
};

interface CreateBoardPopoverProps {
  analyticsSource: SourceType;
  hide: UsePopoverResult<HTMLElement>['hide'];
  pop: UsePopoverResult<HTMLElement>['pop'];
  popoverProps: PopoverProps<HTMLElement>;
  preSelectedWorkspaceId?: string;
  push: UsePopoverResult<HTMLElement>['push'];
  testId?: TestId;
}

export const CreateBoardPopover: React.FC<CreateBoardPopoverProps> = (
  props,
) => {
  const {
    analyticsSource,
    hide,
    pop,
    popoverProps,
    preSelectedWorkspaceId,
    push,
    testId,
  } = props;

  const { currentScreen, isVisible, onBack } = popoverProps;

  const handleBack = useCallback(() => {
    if (currentScreen !== undefined && screenAnalyticsNames[currentScreen]) {
      Analytics.sendClickedButtonEvent({
        buttonName: 'inlineDialogBackButton',
        source: screenAnalyticsNames[currentScreen],
      });
    }

    onBack ? onBack() : pop();
  }, [currentScreen, onBack, pop]);

  const handleHide = useCallback(() => {
    if (currentScreen !== undefined && screenAnalyticsNames[currentScreen]) {
      Analytics.sendClosedComponentEvent({
        componentType: 'inlineDialog',
        componentName: screenAnalyticsNames[currentScreen],
        source: analyticsSource,
      });
    }
    hide();
  }, [analyticsSource, currentScreen, hide]);

  return (
    <Popover
      {...popoverProps}
      onBack={handleBack}
      onHide={handleHide}
      testId={testId}
      title={viewTitle('create')}
    >
      <CreateBoardPopoverScreens
        hide={handleHide}
        isVisible={isVisible}
        preSelectedWorkspaceId={preSelectedWorkspaceId}
        push={push}
      />
    </Popover>
  );
};
